import { debounce } from "lodash";
import {
  useCallback,
  useRef,
  useState,
  MouseEvent,
  KeyboardEvent,
  memo,
} from "react";
import { useSelector } from "react-redux";
import {
  createSelectIsRoomOccupiedByCurrentUserByRoomId,
  createSelectRoomById,
} from "store/rooms/selectors";
import {
  selectIsEventManager,
  selectIsEventSpeaker,
} from "modules/event/selectors";
import { useIsMutating } from "@tanstack/react-query";
import { useAppDispatch } from "store/hooks";
import {
  JoinRoomStatus,
  setJoiningRoomId,
  setJoiningRoomId as setSelectedRoomId,
  updateRoomStatus,
} from "store/rooms";
import { useJoinTable } from "modules/joinTable";
import { selectTheaterTemplate } from "modules/theater/selectors";
import { CONTEXT_MENU_TYPES, SVG_ORIGIN_X, SVG_ORIGIN_Y } from "modules/event";
import { selectIsMobile } from "modules/deviceInfo";
import { ITemplate } from "types/theater";
import { LockTableInfo } from "modules/lockTables/hooks";
import { RoomName } from "modules/svgMapView/templateParts/RoomName";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { MoreVert } from "@remo-co/ui-core/src/icons/MoreVert";
import { useContextMenu } from "modules/contextMenu";
import RoomComponentWrapper from "../RoomComponentWrapper";
import RoomClickableArea from "../RoomClickableArea";
import "./RoomComponent.scss";
import Seats from "../Seats";
import RoomNameEditButton from "../RoomNameEditButton";
import MyRoom from "../MyRoom";
import HostSeatRequestHandler from "../HostSeatRequestHandler";
import { useStyles } from "./styles";

interface Props {
  roomId: string;
  autoAssignRoom: boolean;
  isEventManager: boolean;
  isLockTablesEnabled: boolean;
  lockTableInfo?: LockTableInfo;
  outlined?: boolean;
}

const KEBAB_ICON_COLOR = "white";

type MouseDownCoordinates = {
  x: number;
  y: number;
};

// Allow up to DRAG_THRESHOLD pixels of mouse drag to process a click.
const DRAG_THRESHOLD = 2;

const RoomComponent = memo<Props>(
  ({
    roomId,
    isEventManager,
    isLockTablesEnabled,
    lockTableInfo,
    outlined,
  }) => {
    const room = useSelector(createSelectRoomById(roomId));
    const { x, y, width, height, code, id, boundaryPolygon } = room;
    const styles = useStyles({ isIsometric: !!boundaryPolygon });
    const dispatch = useAppDispatch();

    const spaceConfig = useSelector(selectTheaterTemplate) as ITemplate;

    const { switchTable } = useJoinTable();
    const isJoiningTable = useIsMutating({
      mutationKey: ["remo.switchTable"],
    });

    const origin = spaceConfig?.origin ?? { x: SVG_ORIGIN_X, y: SVG_ORIGIN_Y };
    const roomStyle = {
      left: x + origin.x,
      top: y + origin.y,
      width,
      height,
    };
    const { openContextMenu } = useContextMenu();
    const isRoomOccupiedByCurrentUser = useSelector(
      createSelectIsRoomOccupiedByCurrentUserByRoomId(roomId),
    );
    const [contextMenu, setContextMenu] = useState<JSX.Element | null>(null);
    const [isHovering, setIsHovering] = useState(false);
    const hasEventSpeakerAccess = useSelector(selectIsEventSpeaker);
    const hasEventManagerAccess = useSelector(selectIsEventManager);
    const isMobileView = useSelector(selectIsMobile);
    const clickCoordRef = useRef<MouseDownCoordinates | null>(null);

    const handleRightClick = useCallback(
      async (e: MouseEvent) => {
        if (isMobileView) {
          return;
        }
        e.stopPropagation();
        const menu = await openContextMenu(
          e.clientX,
          e.clientY,
          CONTEXT_MENU_TYPES.ROOM,
          undefined,
          id,
        );

        setContextMenu(menu);
      },
      [id, isMobileView, openContextMenu],
    );

    const handleSingleClick = useCallback(
      (e: MouseEvent) => {
        if (!clickCoordRef.current) {
          return;
        }

        const deltaX = Math.abs(clickCoordRef.current.x - e.clientX);
        const deltaY = Math.abs(clickCoordRef.current.y - e.clientY);

        if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
          return;
        }

        dispatch(setSelectedRoomId(id));
      },
      [dispatch, id],
    );

    const handleMouseDown = useCallback((e: MouseEvent) => {
      clickCoordRef.current = {
        x: e.clientX,
        y: e.clientY,
      };
    }, []);

    const handleDoubleClick = useCallback(
      (e: MouseEvent | KeyboardEvent) => {
        e.stopPropagation();

        if (!isJoiningTable) {
          switchTable({ tableId: id });
          dispatch(setSelectedRoomId(null));
        }
      },
      [dispatch, id, isJoiningTable, switchTable],
    );

    /**
     * Used for mobile room change
     */
    const handleMobileTap = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();

        // don't display join room buttons on private room tap
        if (
          code === "stage" &&
          !(hasEventSpeakerAccess || hasEventManagerAccess)
        ) {
          return;
        }

        dispatch(setJoiningRoomId(id));
        dispatch(updateRoomStatus(JoinRoomStatus.PREPARING));
      },
      [code, hasEventSpeakerAccess, hasEventManagerAccess, dispatch, id],
    );

    const handleKeyPress = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === "Enter" && !isMobileView) {
          handleDoubleClick(e);
        }
      },
      [handleDoubleClick, isMobileView],
    );

    const handleHover = useCallback((isHovering: boolean) => {
      setIsHovering(isHovering);
    }, []);

    return (
      <>
        <RoomComponentWrapper
          isLockTablesEnabled={isLockTablesEnabled}
          tableId={room.id}
          isEventManager={isEventManager}
          code={code}
        >
          <div
            id={`re-${code}`}
            className={styles.mapRoom}
            style={roomStyle}
            role="button"
            tabIndex={0}
            data-testid="room-component-wrapper"
            onKeyDown={handleKeyPress}
          >
            <RoomClickableArea
              handleRightClick={handleRightClick}
              handleDoubleClick={handleDoubleClick}
              handleMobileTap={handleMobileTap}
              handleMouseDown={handleMouseDown}
              handleMouseUp={handleSingleClick}
              handleHover={debounce(handleHover, 500)}
              room={room}
              outlined={outlined}
            />
            <Seats room={room} isHovering={isHovering} />
            <RoomName
              isLockTablesEnabled={isLockTablesEnabled}
              lockTableInfo={lockTableInfo}
              room={room}
              spaceConfig={spaceConfig}
              onRightClick={handleRightClick}
            />
            {contextMenu}
            <div className={styles.hostOnboardingDummy} />
            {!isLockTablesEnabled && isEventManager && (
              <RoomNameEditButton
                room={room}
                className={styles.edit}
                displayPosition={boundaryPolygon ? "right" : "bottom-right"}
              />
            )}
            {!isMobileView && isLockTablesEnabled && (
              <Box
                className={styles.lockTableMovementButton}
                onClick={handleRightClick}
              >
                <MoreVert fontSize="large" htmlColor={KEBAB_ICON_COLOR} />
              </Box>
            )}
          </div>
        </RoomComponentWrapper>
        {isRoomOccupiedByCurrentUser && <MyRoom room={room} />}
        {isRoomOccupiedByCurrentUser && <HostSeatRequestHandler room={room} />}
      </>
    );
  },
);

export default RoomComponent;
